import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '~app/app/~shared/services/snackbar.service';
import { UserService } from '~app/app/~shared/services/user.service';
import { Subject, finalize, takeUntil, tap } from 'rxjs';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { User, UserCreationParams, UserRole } from '~core/models/auth.models';
import { ToastService } from '~app/app/~shared/services/toast.service';
import { USER_ADMIN_ROLES } from '~app/app/~shared/constants/auth.constants';

@Component({
  selector: 'admin-creation-form',
  templateUrl: './admin-creation-form.component.html',
  styleUrls: ['./admin-creation-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminCreationFormComponent
  extends BaseComponent
  implements OnDestroy, OnInit
{
  userService = inject(UserService);
  dialogRef = inject(MatDialogRef<AdminCreationFormComponent>);
  snackbarService = inject(SnackbarService);
  toastService = inject(ToastService);
  user = input<User>();
  userAdminRoles = USER_ADMIN_ROLES;

  isLoading = false;
  registerForm = new FormGroup({
    email: new FormControl<string>('', {
      validators: [Validators.required, Validators.email]
    }),
    roles: new FormControl<string[]>([], [Validators.required]),
    accountType: new FormControl<string>('admin', { validators: [] })
  });

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        violations?.forEach(violation => {
          this.registerForm.get(violation.propertyPath)?.setErrors({
            httpError: violation.title
          });
        });
        this._cdRef.detectChanges();
      }
    })
  );

  private _destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (this.user()) {
      this.registerForm.get('email')?.disable();

      this.registerForm.patchValue({
        email: this.user()?.email,
        roles: this.user()?.roles
      });
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.unsubscribe();
  }

  onSubmit(): void {
    const data = {
      ...this.registerForm.value
    } as UserCreationParams;

    const userId = this.user()?.id;

    if (userId) {
      this.updateUser(userId, data);
    } else {
      this.createUser(data);
    }
  }

  createUser(data: UserCreationParams): void {
    this.isLoading = true;
    this.userService
      .create(data)
      .pipe(
        takeUntil(this._destroy$),
        tap({
          next: () => {
            this.registerForm.reset();
            this.dialogRef.close(true);
            this.snackbarService.open({
              text: `Nouvel utilisateur ajouté avec succès`,
              type: 'success'
            });
          }
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }

  updateUser(id: string, data: UserCreationParams): void {
    this.isLoading = true;
    const roles = data.roles?.filter((r: UserRole) => r !== UserRole.ROLE_USER);

    const formValues = { ...data, roles: roles };

    this.userService
      .update(id, formValues)
      .pipe(
        takeUntil(this._destroy$),
        tap({
          next: updatedUser => {
            this.dialogRef.close(updatedUser);
            this.snackbarService.open({
              text: `Utilisateur modifié avec succès`,
              type: 'success'
            });
          },
          error: err => {
            this.snackbarService.open({
              text: `Erreur de modification: ${err.message}`,
              type: 'error'
            });
          }
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }
}
